import { 
    Typography,
    Box,
    useTheme,
    Grid,
    Card,
    CardContent,
    TextField,
    Stack,
    Button,
    Snackbar,
    ToggleButtonGroup,
    ToggleButton,
    Alert,
    FormControl,
    Select,
    MenuItem,
    FormHelperText
    } from "@mui/material";
import { ArrowBackIos, } from "@mui/icons-material"
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useCallback, useEffect, useMemo, useState } from "react";
import { addCampaign, updateCampaign } from "../../api/apiService";
import { addDays } from "date-fns";

export default function CampaignBuilder ({ editData, onClose }){
    /* fetch clientId from localstorage */
    const clientId = useMemo(() => {
      try {
        return JSON.parse(localStorage.getItem('clientId')) ?? '';
      } catch {
        return '';
      }
    }, []);

    const theme = useTheme();
    const regions = ["North America", "Europe", "Asia", "Australia", "Middle East", "Africa"];
    const companySize = [
        "1-10",
        "11-20",
        "21-50",
        "51-100",
        "101-200",
        "201-500",
        "501-1000",
        "1001-2000",
        "2001-5000",
        "5001-10000",
        "10001+"
    ]

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        audience: "",
        location: "",
        companySize: "",
        scheduledStartDate: null,
        scheduledEndDate: null,
        interval: 2,
        startTime: null, 
        weekdays: [],
        settings:{
            maxFollowUps: 3,
            followUpDelay: 2,    
        },
        targeting: {
            industries: [],
            companySizes: [],
            jobTitles: [],
            regions: [],
            technologies: [],
        }
    });

    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isValid, setIsValid] = useState(false);

    const [loading, setLoading] = useState(false);

    // Validate fields
    const validate = useCallback((values = formData) => {
        const newErrors = {};

        if (!values.name.trim()) newErrors.name = "Required";
        if (!values.description.trim()) newErrors.description = "Required";
        if (!values.audience.trim()) newErrors.audience = "Required";
        if (!values.location.trim()) newErrors.location = "Required";
        if (!values.companySize.trim()) newErrors.companySize = "Required";
        if (!values.scheduledStartDate) newErrors.scheduledStartDate = "Start date required";
        if (!values.scheduledEndDate) newErrors.scheduledEndDate = "End date required";
        if (!formData.startTime) newErrors.startTime = "Start time is required";
        if (!formData.weekdays.length) newErrors.weekdays = "Select at least one weekday";
        if (!values.targeting.jobTitles) newErrors.jobTitles = "Job Title is required";
        if (!values.targeting.regions) newErrors.regions = "Region is required";

        return newErrors;
    }, [formData]);

    //set form for editing
    useEffect(() => {
    if (editData) {
        setFormData({
        name: editData?.name || "",
        description: editData?.description || "",
        audience: editData?.audience || "",
        location: editData?.location || "",
        companySize: editData?.companySize || "",
        scheduledStartDate: editData.scheduledStartDate ? new Date(editData.scheduledStartDate) : null,
        scheduledEndDate: editData.scheduledEndDate ? new Date(editData.scheduledEndDate) : null,
        startTime: editData.startTime ? new Date(editData.startTime) : null,
        weekdays: editData.weekdays || [],
        interval: editData?.interval || 1,
        settings:{
            maxFollowUps: editData.settings?.maxFollowUps || 3,
            followUpDelay: editData.settings?.followUpDelay || 2,
        },
        targeting: {
            industries: editData.targeting?.industries || [],
            companySizes: editData.targeting?.companySizes || [],
            jobTitles: editData.targeting?.jobTitles || [],
            regions: editData.targeting?.regions || [],
            technologies: editData.targeting?.technologies || [],
        }
        });
    }
    }, [editData]);

    /** On schedule campaign button click */
    const handleSubmit = async () => {
        const validationErrors = validate();
        setErrors(validationErrors);
        setTouched({
        name: true,
        description: true,
        audience: true,
        location: true,
        companySize: true,
        scheduledStartDate: true,
        scheduledEndDate: true,
        startTime: true,
        weekdays: true,
        jobTitle: true,
        region: true,
        });
        setLoading(true);

        if (Object.keys(validationErrors).length > 0) return;

        try {
        setIsSubmitting(true);

        if (editData?.id) {
            const payload = {
            ...formData,
            targeting: {
                industries: [formData.audience] || [],
                companySizes: [formData.companySize] || [],
                jobTitles: Array.isArray(formData.targeting.jobTitles)
                    ? formData.targeting.jobTitles
                    : [formData.targeting.jobTitles].filter(Boolean),
                regions: Array.isArray(formData.targeting.regions)
                    ? formData.targeting.regions
                    : [formData.targeting.regions].filter(Boolean),
                technologies: formData.targeting.technologies || [],
            }
            };
            await updateCampaign(editData.id, payload);
             setLoading(false);
             setSnackbar({ open: true, message: 'Campaign updated successfully!', severity: 'success' });
        } else {
            const payload = {
            ...formData,
            clientId:clientId,
            // startDate: new Date(formData.startDate?.$date || formData.startDate).toLocaleDateString(),
            // startTime: new Date(formData.startTime?.$date || formData.startTime).toLocaleTimeString(),
            scheduledStartDate: formData.scheduledStartDate ? formData.scheduledStartDate.toISOString() : null,
            scheduledEndDate: formData.scheduledEndDate ? formData.scheduledEndDate.toISOString() : null,
            startTime: formData.startTime ? formData.startTime.toISOString() : null,
            targeting: {
                industries: [formData.audience] || [],
                companySizes: [formData.companySize] || [],
                jobTitles: Array.isArray(formData.targeting.jobTitles)
                    ? formData.targeting.jobTitles
                    : [formData.targeting.jobTitles].filter(Boolean),
                regions: Array.isArray(formData.targeting.regions)
                    ? formData.targeting.regions
                    : [formData.targeting.regions].filter(Boolean),
                technologies: formData.targeting.technologies || [],
            }
            };
             await addCampaign(payload)
             setLoading(false);
             setSnackbar({ open: true, message: 'Campaign saved successfully!', severity: 'success' });
        }
        setTimeout(() => { onClose(); }, 1200); // 1.2s delay, so that success/error message can show before redirecting
        } catch (err) {
        setLoading(false);
        console.error("Error saving campaign:", err);
        if(err.status === 400){
            setSnackbar({ open: true, message: err.message.message, severity: 'error' });
        } else if(err.status === 403){
            setSnackbar({ open: true, message: err.message?.message, severity: 'error' });
        } else{
            setSnackbar({ open: true, message: 'Error saving campaign', severity: 'error' });
        }
        } finally {
        setIsSubmitting(false);
        }
    };

    //set validation errors
    useEffect(() => {
    const validationErrors = validate();
    setErrors(validationErrors);
    setIsValid(Object.keys(validationErrors).length === 0);
    }, [formData, validate]);

    //on input fields change
    // const handleChange = (e) => {
    //     const { name, value } = e.target;
    //     setFormData((prev) => ({ ...prev, [name]: value }));
    //     setTouched((prev) => ({ ...prev, [name]: true }));

    //     // Validate single field on change
    //     const fieldErrors = validate({ ...formData, [name]: value });
    //     setErrors(fieldErrors);
    // };
    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "maxFollowUps" || name === "followUpDelay") {
            // update nested settings object
            setFormData((prev) => ({
                ...prev,
                settings: { ...prev.settings, [name]: value }
            }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }

        setTouched((prev) => ({ ...prev, [name]: true }));

        // Validate single field on change
        const fieldErrors = validate({
            ...formData,
            [name === "maxFollowUps" || name === "followUpDelay" ? "settings" : name]:
                name === "maxFollowUps" || name === "followUpDelay" ?
                    { ...formData.settings, [name]: value }
                    : value
        });
        setErrors(fieldErrors);
    };

    return(
        <Box sx={{background: (theme) => theme.palette.gradient.surface, minHeight: '100%', height:'100%'}} >
            {/* Error display via Alert and snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                <Alert
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
            {/* Content heading */}
            <Grid
                sx={{ display: "flex", justifyContent: "space-between", alignItems: "center"}}
            >
                <Typography variant="h5" fontWeight={600}>
                    {editData? 'Edit Campaign' : 'Campaign Builder'}
                </Typography>
                <Button
                    size="medium"
                    variant="outlined"
                    startIcon={<ArrowBackIos />}
                    sx={{ borderRadius: 2, textTransform: "none" }}
                    onClick={onClose}
                >
                    Back 
                </Button>
            </Grid>
            
            {/* Content caption text */}
            <Typography variant="caption" fontWeight={400} color={theme.palette.neutral.normal}>
                {editData? 'Update the campaign' : 'Create your new campaign'}
            </Typography>
            <Grid container spacing={2} sx={{pt:2}}>
                {/* Campaign Basics*/}
                <Grid size={{xs:12, sm:12, md:8}}>
                    <Card elevation={0} sx={{borderRadius:2, px:2}}>
                        <CardContent>
                            <Typography sx={{py:1 }} variant="body1" fontWeight={600} color={theme.palette.neutral}>
                                Campaign Basics
                            </Typography>
                            <Stack flexDirection="column">
                                {/* Campaign name */}
                                <Box>
                                    <Typography variant="body2" fontWeight={500} sx={{py:1}}>Name</Typography>
                                    <Box component="form" autoComplete="off">
                                        <TextField
                                        name="name"
                                        placeholder="Campaign Name"
                                        fullWidth
                                        size="small"
                                        value={formData.name}
                                        onChange={handleChange}
                                        slotProps={{ htmlInput:{maxLength:100} }}
                                        error={touched.name && !!errors.name}
                                        helperText={touched.name && errors.name}
                                        />                                    
                                    </Box>
                                </Box>
                                {/* Campaign description */}
                                <Box>
                                    <Typography variant="body2" fontWeight={500} sx={{py:1}}>Description</Typography>
                                    <Box component="form" autoComplete="off">
                                       <TextField
                                        name="description"
                                        placeholder="Campaign Description"
                                        fullWidth
                                        multiline
                                        rows={3}
                                        value={formData.description}
                                        onChange={handleChange}
                                        slotProps={{ htmlInput:{maxLength:1000} }}
                                        error={touched.description && !!errors.description}
                                        helperText={touched.description && errors.description}
                                        />
                                    </Box>
                                </Box>
                                {/* Audience / Industry */}
                                <Box>
                                    <Typography variant="body2" fontWeight={500} sx={{py:1}}>Industry</Typography>
                                    <Box component="form" autoComplete="off">
                                        <TextField
                                        name="audience"
                                        //label="Target Audience"
                                        placeholder="Industry"
                                        fullWidth
                                        size="small"
                                        value={formData.audience}
                                        onChange={handleChange}
                                        slotProps={{ htmlInput:{maxLength:100} }}
                                        error={touched.audience && !!errors.audience}
                                        helperText={touched.audience && errors.audience}
                                        />      
                                    </Box>
                                </Box>
                                {/* Job Title */}
                                <Box>
                                    <Box component="form" autoComplete="off">
                                    <Typography variant="body2" fontWeight={500} sx={{ py: 1 }}>Job Title</Typography>
                                    <TextField
                                        name="jobTitle"
                                        placeholder="Job Title"
                                        fullWidth
                                        size="small"
                                        value={formData.targeting.jobTitles}
                                        onChange={(e) =>
                                            // setFormData(prev => ({
                                            //     ...prev,
                                            //    targeting: { ...(prev.targeting || {}), jobTitles: e.target.value }
                                            // }))
                                            setFormData(prev => ({
                                                ...prev,
                                                targeting: {
                                                    ...(prev.targeting ?? {}),
                                                    jobTitles: [e.target.value]
                                                }
                                            }))
                                        }
                                        error={touched.jobTitle && !!errors.jobTitles}
                                        helperText={touched.jobTitle && errors.jobTitles}
                                    />
                                    </Box>
                                </Box>
                                {/* CompanySize */}
                                <Box>
                                    <Typography variant="body2" fontWeight={500} sx={{ py: 1 }}>Company Size</Typography>
                                    <Box component="form" autoComplete="off">
                                        <FormControl fullWidth size="small" error={touched.companySize && !!errors.companySize}>
                                            <Select
                                                displayEmpty
                                                name="companySize"
                                                value={formData.companySize}
                                                onChange={handleChange}
                                                renderValue={(selected) => {
                                                    if (!selected) {
                                                        return <em>Select Company Size</em>; // Placeholder
                                                    }
                                                    return selected;
                                                }}
                                            >
                                                <MenuItem disabled value="">
                                                    <em>Select Company Size</em>
                                                </MenuItem>
                                                {companySize.map((r) => (
                                                    <MenuItem key={r} value={r}>{r}</MenuItem>
                                                ))}
                                            </Select>
                                            {touched.companySize && errors.companySize && (
                                                <FormHelperText>{errors.companySize}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Box>
                                </Box>
                                {/* Location */}
                                <Box>
                                    <Typography variant="body2" fontWeight={500} sx={{py:1}}>Location</Typography>
                                    <Box component="form" autoComplete="off">
                                        <TextField
                                        name="location"
                                        //label="Location"
                                        placeholder="Location"
                                        fullWidth
                                        size="small"
                                        value={formData.location}
                                        onChange={handleChange}
                                        slotProps={{ htmlInput:{maxLength:100} }}
                                        error={touched.location && !!errors.location}
                                        helperText={touched.location && errors.location}
                                        />   
                                    </Box>
                                </Box>
                                {/* Region */}
                                <Box>
                                    <Typography variant="body2" fontWeight={500} sx={{ py: 1 }}>Region</Typography>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            displayEmpty
                                            value={formData.targeting.regions}
                                            onChange={(e) =>
                                                // setFormData(prev => ({
                                                //     ...prev,
                                                //     targeting: { ...(prev.targeting || {}), regions: e.target.value }
                                                // }))
                                                setFormData(prev => ({
                                                    ...prev,
                                                    targeting: {
                                                        ...(prev.targeting ?? {}), 
                                                        regions: [e.target.value]
                                                    }
                                                }))
                                            }
                                        >
                                            <MenuItem disabled value="">
                                                <em>Select Region</em>
                                            </MenuItem>
                                            {regions.map((r) => (
                                                <MenuItem key={r} value={r}>{r}</MenuItem>
                                            ))}
                                        </Select>
                                        {touched.region && errors.regions && (
                                                <FormHelperText>{errors.regions}</FormHelperText>
                                            )}
                                    </FormControl>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
                {/*  Campaign Settings */}
                <Grid size={{xs:12, sm:12, md:4}}>
                    <Card elevation={0} sx={{borderRadius:2, px:2}}>
                        <CardContent>
                            <Typography sx={{py:1}}  variant="body1" fontWeight={600} color={theme.palette.neutral}>
                                Campaign Settings
                            </Typography>
                            <Stack flexDirection="column" gap={2}>
                                <Box >
                                    <Typography variant="body2" fontWeight={500} sx={{py:1}}>Schedule</Typography>
                                </Box>
                                {/* Start Date */}
                                <Box>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                        label="Start Date"
                                        value={formData.scheduledStartDate}
                                        minDate={new Date()} //should not accept date for past days
                                        onChange={(newDate) => {
                                            setFormData((prev) => ({ ...prev, scheduledStartDate: newDate }));
                                            setTouched((prev) => ({ ...prev, scheduledStartDate: true }));
                                        }}
                                        slotProps={{
                                            textField: {
                                            fullWidth: true,
                                            size: "small",
                                            error: touched.scheduledStartDate && !!errors.scheduledStartDate,
                                            helperText: touched.scheduledStartDate && errors.scheduledStartDate,
                                            },
                                        }}
                                        />
                                    </LocalizationProvider>
                                </Box>
                                {/* End Date */}
                                <Box>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                        label="End Date"
                                        value={formData.scheduledEndDate}
                                        minDate={formData.scheduledStartDate || new Date()} //should not accept date for past start date or past days
                                        // max date = start date + 60 days
                                        maxDate={
                                            formData.scheduledStartDate
                                                ? addDays(formData.scheduledStartDate, 60)
                                                : addDays(new Date(), 60)
                                        }
                                        onChange={(newDate) => {
                                            setFormData((prev) => ({ ...prev, scheduledEndDate: newDate }));
                                            setTouched((prev) => ({ ...prev, scheduledEndDate: true }));
                                        }}
                                        slotProps={{
                                            textField: {
                                            fullWidth: true,
                                            size: "small",
                                            error: touched.scheduledEndDate && !!errors.scheduledEndDate,
                                            helperText: touched.scheduledEndDate && errors.scheduledEndDate,
                                            },
                                        }}
                                        />
                                    </LocalizationProvider>
                                </Box> 
                               
                                <Typography variant="body2" fontWeight={500} sx={{ mt: 2 }}>
                                Trigger interval
                                </Typography>
                                <FormControl fullWidth >
                                    <Select
                                    size="small"
                                    value={formData.interval}
                                    onChange={(e) => {
                                        const newInterval = e.target.value;

                                        setFormData((prev) => ({
                                            ...prev,
                                            interval: newInterval,
                                            // If interval is 1 (daily), reset weekdays to all days; otherwise preserve or clear weekdays
                                            weekdays: newInterval === 1 ? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] : prev.weekdays,

                                        }));
                                    }}
                                    >
                                    <MenuItem value={1}>Daily</MenuItem>
                                    <MenuItem value={2}>Weekly</MenuItem>
                                    </Select>
                                </FormControl>

                            {formData.interval===2 &&   
                             <> 
                                <ToggleButtonGroup
                                value={formData.weekdays}
                                onChange={(e, newWeekdays) => {
                                    setFormData((prev) => ({ ...prev, weekdays: newWeekdays || []}));
                                    setTouched((prev) => ({ ...prev, weekdays: true }));
                                }}
                                aria-label="weekdays"
                                size="small"
                                fullWidth
                                color="primary"
                                >
                                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                                    <ToggleButton sx={{overflow:"hidden"}}  key={day} value={day} aria-label={day}>
                                    {day}
                                    </ToggleButton>
                                ))}
                                </ToggleButtonGroup> 
                            </>
                            }

                                <Box>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <TimePicker
                                        label="Start Time"
                                        value={formData.startTime}
                                        onChange={(newTime) => {
                                        setFormData((prev) => ({ ...prev, startTime: newTime }));
                                        setTouched((prev) => ({ ...prev, startTime: true }));
                                        }}
                                        slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: "small",
                                            error: touched.startTime && !!errors.startTime,
                                            helperText: touched.startTime && errors.startTime,
                                        },
                                        }}
                                    />
                                    </LocalizationProvider>                                   
                                </Box>
                                <Box>
                                    <Typography variant="body2" fontWeight={500} sx={{ py: 1 }}>
                                        Max Follow-Ups
                                    </Typography>
                                    <TextField
                                        name="maxFollowUps"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        value={formData.settings.maxFollowUps}
                                        onChange={handleChange}
                                        InputProps={{ inputProps: { min: 1, max: 5 } }}
                                    />
                                </Box>

                                <Box>
                                    <Typography variant="body2" fontWeight={500} sx={{ py: 1 }}>
                                        Follow-Up Delay (days)
                                    </Typography>
                                    <TextField
                                        name="followUpDelay"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        value={formData.settings.followUpDelay}
                                        onChange={handleChange}
                                        InputProps={{ inputProps: { min: 1, max: 5 } }}
                                    />
                                </Box>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    sx={{ mt: 2 }}
                                    loading={loading}
                                    loadingPosition="end"
                                    onClick={handleSubmit}
                                    disabled={!isValid || isSubmitting}
                                >
                                    {isSubmitting ? "Scheduling..." : "Schedule Campaign"}
                                </Button>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}